import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayerTypes, SourceItem } from '@redux/map/types';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { useDispatch } from 'react-redux';
import {
    changedLayerVisibility,
    removedSource,
    updatedMapboxLayersOrder,
} from '@redux/map/map-reducer';
import './layers-list.scss';
import { useTranslation } from 'react-i18next';
import {
    selectAllLayersById,
    selectAllLayersIdsBySourceId,
    selectLayersGroups,
    selectListLayers,
    selectSourceById,
} from '@redux/selectors/selectors';
import LoadingSpinner from '@components/Spinner/LoadingSpinner';
import { isLayerVisibleInList, isLayerVisibleOnMap } from '@utils/mapUtils';
import { useTheme } from '@context/themeContext';
import { AlertRhombusIcon } from '@components/icons/icons/AlertRhombusIcon';
import Tooltip from '@mui/material/Tooltip';
import { closestCenter, DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core/dist/types';
import LayersListGroup from './LayersListGroup';
import DraggableItem from '@components/DraggableItem/DraggableItem';
import { isErrorResponse, isUuidString } from '@utils/utils';
import { themeColors } from '@styles/theme';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import { DeleteIcon } from '@components/icons/icons/DeleteIcon';
import { LockClosedIcon } from '@components/icons/icons/LockClosedIcon';
import { LockOpenedIcon } from '@components/icons/icons/LockOpenedIcon';
import { useMapLayersClickabilityContext } from '@context/mapLayersClickability';
import NewLayerFileUploader from '../new-layer-file-uploader/NewLayerFileUploader';
import { NewLayerFromPreset } from '../new-layer-from-preset/ui/NewLayerFromPreset';
import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal';
import { entityApi } from '@api/entityApi';
import { ApiError } from '@api/RestClient';
import { useToastContext } from '@context/toastContext';

const READ_ONLY_LAYER_ENTITY_NAME = 'replancity_ReadOnlyLayerEntity';

const isUserExternalLayerSource = (source?: SourceItem): boolean => !!source?.deletable;

type RowProps<T> = {
    rowItem: T;
    visible: boolean;
    dayTimeDependent?: boolean;
    deleting?: boolean;
    onDeleteClick?: (source: SourceItem) => void;
    draggable?: boolean;

    style?: any;
    attributes?: any;
    listeners?: any;
};

type LayerRowProps = RowProps<LayerTypes>;

export const LayerRow = React.memo(
    React.forwardRef(
        (
            { rowItem, visible, dayTimeDependent, deleting, onDeleteClick, ...rest }: LayerRowProps,
            ref: React.ForwardedRef<HTMLDivElement>
        ) => {
            const mounted = useRef<boolean>(false);
            const layersIdsBySourceId = useTypedSelector(selectAllLayersIdsBySourceId);
            const sourceItem = useTypedSelector(state => selectSourceById(state, rowItem.source));
            const { lockLayer } = useMapLayersClickabilityContext();
            const dispatch = useDispatch();
            const { t } = useTranslation();
            const { theme } = useTheme();

            const { id, source, layerName, clickable, alwaysLocked, loading, isLockBtnVisible } =
                rowItem;

            const lockIconColor = useMemo(
                () =>
                    theme === 'light'
                        ? clickable
                            ? themeColors.navyBlue
                            : themeColors.grey3
                        : clickable
                          ? themeColors.white
                          : themeColors.grey3,
                [clickable]
            );
            const lockIconBgColor = theme === 'light' ? themeColors.lightGrey : themeColors.grey4;

            useEffect(() => {
                if (mounted.current) {
                    dispatch(changedLayerVisibility({ layerId: id, visible }));
                } else {
                    mounted.current = true;
                }
            }, [visible]);

            const changeVisibility = useCallback(
                ({ target }) => {
                    layersIdsBySourceId[source].map(id => {
                        dispatch(changedLayerVisibility({ layerId: id, visible: target.checked }));
                    });
                },
                [source, changedLayerVisibility, layersIdsBySourceId]
            );

            const canDelete = isUserExternalLayerSource(sourceItem);

            return (
                <div
                    id={id}
                    className="layer-row-item layer-row-item_colored"
                    ref={ref}
                    data-testid="layersListRow"
                    {...rest}
                >
                    <div className="row-check">
                        {loading ? (
                            <LoadingSpinner size="13px" backgroundColor={'transparent'} />
                        ) : (
                            <input type="checkbox" checked={visible} onChange={changeVisibility} />
                        )}
                    </div>
                    <div className="row-name" draggable>
                        <span>{layerName}</span>
                    </div>
                    <div className="layer-row-item__controls">
                        {dayTimeDependent ? (
                            <Tooltip
                                title={t('layers-list.daytime-dependent-layer-warning')}
                                placement="top"
                            >
                                <div className="alert">
                                    <AlertRhombusIcon />
                                </div>
                            </Tooltip>
                        ) : null}
                        {isLockBtnVisible ? (
                            <Tooltip
                                title={
                                    alwaysLocked
                                        ? t('layers-list.layer-always-locked-tooltip')
                                        : clickable
                                          ? t('layers-list.layer-lock-tooltip')
                                          : t('layers-list.layer-unlock-tooltip')
                                }
                                placement="top"
                            >
                                <IconButton
                                    icon={
                                        clickable && !alwaysLocked ? LockOpenedIcon : LockClosedIcon
                                    }
                                    iconColor={lockIconColor}
                                    backgroundColor={lockIconBgColor}
                                    width={24}
                                    height={24}
                                    onClick={() =>
                                        lockLayer({
                                            layerId: id,
                                            sourceId: source,
                                            isInteractive: !clickable && !alwaysLocked,
                                        })
                                    }
                                />
                            </Tooltip>
                        ) : null}
                        {canDelete ? (
                            <Tooltip title={t('common.delete')} placement="top">
                                <span>
                                    <IconButton
                                        icon={DeleteIcon}
                                        width={24}
                                        height={24}
                                        onClick={() => sourceItem && onDeleteClick?.(sourceItem)}
                                        iconColor={themeColors.textGrey}
                                        borderless
                                        disabled={deleting}
                                        testId="deleteExternalLayerBtn"
                                    />
                                </span>
                            </Tooltip>
                        ) : null}
                    </div>
                </div>
            );
        }
    )
);

export const LayersList = () => {
    const [allLayersChecked, setAllLayersChecked] = useState<boolean>(false);
    const [sourceToDelete, setSourceToDelete] = useState<SourceItem | null>(null);
    const [deletingSourceIds, setDeletingSourceIds] = useState<Record<string, boolean>>({});
    const allLayersById = useTypedSelector(selectAllLayersById);
    const listLayers = useTypedSelector(selectListLayers);
    const layersGroups = useTypedSelector(selectLayersGroups);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { addToast } = useToastContext();

    useEffect(() => {
        const allChecked = Object.values(allLayersById).every(layer => {
            return isLayerVisibleOnMap(layer);
        });

        setAllLayersChecked(allChecked);
    }, [allLayersById]);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 10,
            },
        })
    );

    const allLayersSelectionHandler = ({ target }) => {
        Object.values(allLayersById).map(layer =>
            dispatch(
                changedLayerVisibility({
                    layerId: layer.id,
                    visible: target.checked,
                })
            )
        );
    };

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                let oldIndex = 0,
                    newIndex = 0;

                listLayers.layersIds.forEach((id, ind) => {
                    if (id === active.id) {
                        oldIndex = ind;
                    }
                    if (id === over.id) {
                        newIndex = ind;
                    }
                });

                dispatch(updatedMapboxLayersOrder({ oldIndex, newIndex }));
            }
        },
        [listLayers]
    );

    const handleDeleteClick = useCallback((source: SourceItem) => {
        setSourceToDelete(source);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!sourceToDelete || deletingSourceIds[sourceToDelete.id]) {
            return;
        }

        const abortController = new AbortController();

        setDeletingSourceIds(prev => ({ ...prev, [sourceToDelete.id]: true }));

        const resp = await entityApi.deleteEntity({
            entityName: READ_ONLY_LAYER_ENTITY_NAME,
            entityId: sourceToDelete.id,
            abortSignal: abortController.signal,
        });

        setDeletingSourceIds(prev => {
            const next = { ...prev };
            delete next[sourceToDelete.id];
            return next;
        });

        if (isErrorResponse(resp)) {
            const error = resp as ApiError;
            const message =
                error.status === 404
                    ? t('layers-list.delete_layer_not_found', {
                          defaultValue: 'Layer was not found or was already deleted',
                      })
                    : error.error_description || t('common.delete-error');

            addToast(message, 'error');
            return;
        }

        dispatch(removedSource(sourceToDelete.id));
        addToast(t('common.delete-successful'), 'success');
    }, [sourceToDelete, deletingSourceIds, dispatch, addToast, t]);

    const handleDeleteModalShowChange = useCallback((show: React.SetStateAction<boolean>) => {
        if (typeof show === 'function') {
            setSourceToDelete(prev => (show(!!prev) ? prev : null));
            return;
        }

        if (!show) {
            setSourceToDelete(null);
        }
    }, []);

    return (
        <div className={`layers-list-container`} data-testid="layersList">
            <DndContext
                sensors={sensors}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={listLayers.layersIds}
                    strategy={verticalListSortingStrategy}
                >
                    {listLayers.layersIds.some(id => isUuidString(id)) ? (
                        <>
                            <div className="layers-list-header">
                                <div className="row-check">
                                    <input
                                        type="checkbox"
                                        checked={allLayersChecked}
                                        onChange={allLayersSelectionHandler}
                                    />
                                </div>
                                <div className="row-name">
                                    <span>{t('layers-list.all-layers')}</span>
                                </div>
                                <div className="layers-list-header-controls">
                                    <NewLayerFileUploader />
                                    <NewLayerFromPreset />
                                </div>
                            </div>
                            {layersGroups.map((group, groupInd) => {
                                let noVisibleLayers = true;

                                const layersElements = listLayers.layersByGroup[group].map(
                                    (layer, ind) => {
                                        const { id, dayTimeDependent } = layer;

                                        if (!isLayerVisibleInList(layer, layer.state)) {
                                            return (
                                                <React.Fragment
                                                    key={`${layer.id}_${ind}`}
                                                ></React.Fragment>
                                            );
                                        }

                                        noVisibleLayers = false;

                                        return (
                                            <DraggableItem<LayerRowProps>
                                                key={`${id}_${ind}`}
                                                id={id}
                                                Component={LayerRow}
                                                props={{
                                                    rowItem: layer,
                                                    visible: isLayerVisibleOnMap(layer),
                                                    dayTimeDependent,
                                                    deleting: deletingSourceIds[layer.source],
                                                    onDeleteClick: handleDeleteClick,
                                                }}
                                            />
                                        );
                                    }
                                );

                                return (
                                    <React.Fragment key={groupInd}>
                                        {noVisibleLayers ? null : (
                                            <LayersListGroup title={group}>
                                                {layersElements}
                                            </LayersListGroup>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </>
                    ) : (
                        t('map.no-layers-exist-warning')
                    )}
                </SortableContext>
            </DndContext>
            {sourceToDelete ? (
                <ConfirmationModal
                    title={t('common.delete-confirmation')}
                    confirmBtnText={t('common.delete')}
                    cancelBtnText={t('common.cancel')}
                    setModalShow={handleDeleteModalShowChange}
                    onConfirmFn={handleDeleteConfirm}
                />
            ) : null}
        </div>
    );
};

export default LayersList;
